import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Slider from 'react-slick';
import ReactPlayer from 'react-player/lazy';

import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

const RootContainer = styled.div`
  padding: 16px 16px 0;
`;

const StyledSlider = styled(Slider)`
  width: 100%;
  border-radius: 8px;
  overflow: hidden;

  .slick-track {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .slick-slide {
    height: 100%;
    background-color: #FFF;
  }
`;

const AspectRatioConatiner = styled.div`
  position: relative;
  overflow: hidden;
  padding-bottom: 49.56%;
`;

const AssetContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const CustomSlide = ({ children }) => (
  <AspectRatioConatiner>
    <AssetContainer>
      {children}
    </AssetContainer>
  </AspectRatioConatiner>
);

CustomSlide.propTypes = {
  children: PropTypes.node.isRequired,
};

const StyledAnchor = styled.a`
  margin: 0;
  text-decoration: none;
  user-select: all;
  pointer-events: all;
  flex: 0 0 auto;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const StyledImg = styled.img`
  flex: 0 0 auto;
  height: 100%;
`;

const StyledIframe = styled.iframe`
  height: 100%;
  width: 100%;
  user-select: all;
  pointer-events: all;
`;

const SliderBulletList = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 8px 0;
`;

const SliderBulletContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
`;

const SliderBullet = styled.div`
  width: 8px;
  height: 2px;
  background: #484848;
  opacity: ${({ active }) => (active ? '1' : '0.2')};
  border-radius: 4px;
`;

const MobileBannerView = ({ assets }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const sliderRef = React.useRef();
  const reactPlayerRef = React.useRef();

  const onSlideChange = (index) => {
    setActiveIndex(index);
    const player = document.getElementById('videoPlayer');
    if (player?.tagName?.toLowerCase() === 'video' && !player?.paused) {
      player?.pause();
      reactPlayerRef?.current?.showPreview();
      sliderRef?.current?.slickPlay();
    }
  };

  return (
    <RootContainer>
      <StyledSlider
        autoplay
        arrows={false}
        ref={sliderRef}
        dots={false}
        afterChange={onSlideChange}
        pauseOnHover={false}
      >
        {
          assets.map(({
            src,
            type,
            href,
            thumbnail,
          }, index) => {
            switch (type) {
              case 'embed':
                return (
                  <CustomSlide key={index}>
                    <StyledIframe src={src} allow="fullscreen" />
                  </CustomSlide>
                );
              case 'video':
                return (
                  <CustomSlide key={index}>
                    <ReactPlayer
                      url={src}
                      ref={reactPlayerRef}
                      light={thumbnail || true}
                      config={{
                        file: {
                          attributes: {
                            controls: true,
                            controlsList: 'nodownload noremoteplayback noremoteplayback',
                            disablePictureInPicture: true,
                            onPlay: () => {
                              sliderRef?.current?.slickPause();
                            },
                            onLoadStart: () => {
                              sliderRef?.current?.slickPause();
                            },
                            id: 'videoPlayer',
                            onCanPlay: () => {
                              sliderRef?.current?.slickPause();
                            },
                            preload: 'metadata',
                            onPause: () => {
                              sliderRef?.current?.slickPlay();
                            },
                            autoPlay: true,
                          },
                        },
                      }}
                      height="100%"
                      width="100%"
                      style={{
                        userSelect: 'all',
                        pointerEvents: 'all',
                        backgroundColor: 'black',
                      }}
                    />
                  </CustomSlide>
                );
              case 'image':
              default:
                return (
                  <CustomSlide key={index}>
                    {
                      href && href.trim() !== '' ? (
                        <StyledAnchor href={href} target="_blank" rel="noopener noreferrer">
                          <StyledImg src={src} />
                        </StyledAnchor>
                      ) : (
                        <StyledImg src={src} />
                      )
                    }
                  </CustomSlide>
                );
            }
          })
        }
      </StyledSlider>
      <SliderBulletList>
        {
          assets.map((_, index) => (
            <SliderBulletContainer key={index}>
              <SliderBullet active={index === activeIndex} />
            </SliderBulletContainer>
          ))
        }
      </SliderBulletList>
    </RootContainer>
  );
};

MobileBannerView.propTypes = {
  assets: PropTypes.arrayOf(PropTypes.shape({
    src: PropTypes.string,
    type: PropTypes.string,
  })),
};

MobileBannerView.defaultProps = {
  assets: [],
};

export default MobileBannerView;
